// OpenAI API integration for GameDay Travel Assistant
// Add your OpenAI API key to the environment variable REACT_APP_OPENAI_API_KEY

import { getSeasonLabel } from './season';

// Check if OpenAI is configured (will check server-side)
export const isOpenAIConfigured = () => {
  // Always return true since we'll check server-side via proxy
  return true;
};

// Determine if the team is NFL or college based on the team name
const getTeamType = (teamName) => {
  const nflTeams = [
    "Arizona Cardinals", "Atlanta Falcons", "Baltimore Ravens", "Buffalo Bills", "Carolina Panthers", 
    "Chicago Bears", "Cincinnati Bengals", "Cleveland Browns", "Dallas Cowboys", "Denver Broncos",
    "Detroit Lions", "Green Bay Packers", "Houston Texans", "Indianapolis Colts", "Jacksonville Jaguars",
    "Kansas City Chiefs", "Las Vegas Raiders", "Los Angeles Chargers", "Los Angeles Rams", "Miami Dolphins",
    "Minnesota Vikings", "New England Patriots", "New Orleans Saints", "New York Giants", "New York Jets",
    "Philadelphia Eagles", "Pittsburgh Steelers", "San Francisco 49ers", "Seattle Seahawks", 
    "Tampa Bay Buccaneers", "Tennessee Titans", "Washington Commanders"
  ];
  
  const mlbTeams = [
    "Arizona Diamondbacks", "Atlanta Braves", "Baltimore Orioles", "Boston Red Sox", "Chicago Cubs",
    "Chicago White Sox", "Cincinnati Reds", "Cleveland Guardians", "Colorado Rockies", "Detroit Tigers",
    "Houston Astros", "Kansas City Royals", "Los Angeles Angels", "Los Angeles Dodgers", "Miami Marlins",
    "Milwaukee Brewers", "Minnesota Twins", "New York Mets", "New York Yankees", "Oakland Athletics",
    "Philadelphia Phillies", "Pittsburgh Pirates", "San Diego Padres", "San Francisco Giants", "Seattle Mariners",
    "St. Louis Cardinals", "Tampa Bay Rays", "Texas Rangers", "Toronto Blue Jays", "Washington Nationals"
  ];
  
  const nbaTeams = [
    "Atlanta Hawks", "Boston Celtics", "Brooklyn Nets", "Charlotte Hornets", "Chicago Bulls",
    "Cleveland Cavaliers", "Dallas Mavericks", "Denver Nuggets", "Detroit Pistons", "Golden State Warriors",
    "Houston Rockets", "Indiana Pacers", "LA Clippers", "Los Angeles Lakers", "Memphis Grizzlies",
    "Miami Heat", "Milwaukee Bucks", "Minnesota Timberwolves", "New Orleans Pelicans", "New York Knicks",
    "Oklahoma City Thunder", "Orlando Magic", "Philadelphia 76ers", "Phoenix Suns", "Portland Trail Blazers",
    "Sacramento Kings", "San Antonio Spurs", "Toronto Raptors", "Utah Jazz", "Washington Wizards"
  ];
  
  const nhlTeams = [
    "Anaheim Ducks", "Arizona Coyotes", "Boston Bruins", "Buffalo Sabres", "Calgary Flames",
    "Carolina Hurricanes", "Chicago Blackhawks", "Colorado Avalanche", "Columbus Blue Jackets", "Dallas Stars",
    "Detroit Red Wings", "Edmonton Oilers", "Florida Panthers", "Los Angeles Kings", "Minnesota Wild",
    "Montreal Canadiens", "Nashville Predators", "New Jersey Devils", "New York Islanders", "New York Rangers",
    "Ottawa Senators", "Philadelphia Flyers", "Pittsburgh Penguins", "San Jose Sharks", "Seattle Kraken",
    "St. Louis Blues", "Tampa Bay Lightning", "Toronto Maple Leafs", "Vancouver Canucks", "Vegas Golden Knights",
    "Washington Capitals", "Winnipeg Jets"
  ];
  
  if (nflTeams.some(team => teamName.includes(team))) return 'NFL';
  if (mlbTeams.some(team => teamName.includes(team))) return 'MLB';
  if (nbaTeams.some(team => teamName.includes(team))) return 'NBA';
  if (nhlTeams.some(team => teamName.includes(team))) return 'NHL';
  
  return 'College';
};

// Get schedule verification instructions based on team type
const getScheduleVerificationInstructions = (teamType) => {
  switch (teamType) {
    case 'NFL':
      return 'This is an NFL game, so check the official NFL schedule and verify week number, playoff status, and broadcast details. Do NOT look at other sports schedules.';
    case 'MLB':
      return 'This is an MLB game, so check the official MLB schedule and verify game time, playoff implications, and broadcast details. Do NOT look at other sports schedules.';
    case 'NBA':
      return 'This is an NBA game, so check the official NBA schedule and verify game time, playoff implications, and broadcast details. Do NOT look at other sports schedules.';
    case 'NHL':
      return 'This is an NHL game, so check the official NHL schedule and verify game time, playoff implications, and broadcast details. Do NOT look at other sports schedules.';
    default:
      return 'This is a college football game, so check college football schedules including conference games, rivalry weeks, and bowl game assignments. Do NOT look at professional sports schedules.';
  }
};

// Get season context based on team type
const getSeasonContext = (teamType, gameInfo) => {
  switch (teamType) {
    case 'NFL':
      return `Week/Season context: Regular season, playoffs, or postseason`;
    case 'MLB':
      return `Season context: Regular season, playoffs, or World Series`;
    case 'NBA':
      return `Season context: Regular season, playoffs, or NBA Finals`;
    case 'NHL':
      return `Season context: Regular season, playoffs, or Stanley Cup Finals`;
    default:
      return `Conference/Rivalry context: ${gameInfo.specialGame || 'Conference game'}`;
  }
};

// Get league-specific context
const getLeagueContext = (teamType) => {
  switch (teamType) {
    case 'NFL':
      return 'NFL CONTEXT: Focus on professional stadium experience, NFL fan culture, tailgating traditions, and the unique atmosphere of professional football. Consider game importance (division rivalry, playoff implications, prime time), weather factors, and stadium-specific experiences.';
    case 'MLB':
      return 'MLB CONTEXT: Focus on baseball stadium experience, MLB fan culture, ballpark traditions, and the unique atmosphere of professional baseball. Consider game importance (division rivalry, playoff implications, day/night games), weather factors, and stadium-specific experiences.';
    case 'NBA':
      return 'NBA CONTEXT: Focus on basketball arena experience, NBA fan culture, arena traditions, and the unique atmosphere of professional basketball. Consider game importance (division rivalry, playoff implications, prime time), and arena-specific experiences.';
    case 'NHL':
      return 'NHL CONTEXT: Focus on hockey arena experience, NHL fan culture, arena traditions, and the unique atmosphere of professional hockey. Consider game importance (division rivalry, playoff implications, prime time), and arena-specific experiences.';
    default:
      return 'COLLEGE CONTEXT: Focus on campus atmosphere, college traditions, student sections, alma mater connections, and the unique energy of college football. Consider rivalry significance, homecoming, conference championship implications, and campus-specific experiences.';
  }
};

// Get schedule notes based on team type
const getScheduleNotes = (teamType) => {
  switch (teamType) {
    case 'NFL':
      return 'playoff implications, or broadcast details';
    case 'MLB':
      return 'playoff implications, or broadcast details';
    case 'NBA':
      return 'playoff implications, or broadcast details';
    case 'NHL':
      return 'playoff implications, or broadcast details';
    default:
      return 'rivalry significance, bowl implications, or conference standings';
  }
};

// Get atmosphere description based on team type
const getAtmosphereDescription = (teamType) => {
  switch (teamType) {
    case 'NFL':
      return 'professional atmosphere, tailgating culture, stadium features';
    case 'MLB':
      return 'ballpark atmosphere, baseball traditions, stadium features';
    case 'NBA':
      return 'arena atmosphere, basketball culture, arena features';
    case 'NHL':
      return 'arena atmosphere, hockey culture, arena features';
    default:
      return 'rivalries, traditions, campus energy, student sections';
  }
};

// Get ticket advice based on team type
const getTicketAdvice = (teamType) => {
  switch (teamType) {
    case 'NFL':
      return 'NFL stadium experience';
    case 'MLB':
      return 'MLB ballpark experience';
    case 'NBA':
      return 'NBA arena experience';
    case 'NHL':
      return 'NHL arena experience';
    default:
      return 'college game atmosphere';
  }
};

// Generate travel plan using OpenAI with live schedule verification
export const generateTravelPlan = async (gameInfo, travelFrom, selectedTeam) => {

  const teamType = getTeamType(selectedTeam.name);
  
  // Determine season and league context based on team type
  let currentSeason, leagueContext;
  switch (teamType) {
    case 'NFL':
      currentSeason = getSeasonLabel('NFL');
      leagueContext = 'professional NFL football with 17 regular season games, playoffs, and Super Bowl';
      break;
    case 'MLB':
      currentSeason = getSeasonLabel('MLB');
      leagueContext = 'professional Major League Baseball with 162 regular season games, playoffs, and World Series';
      break;
    case 'NBA':
      currentSeason = getSeasonLabel('NBA');
      leagueContext = 'professional NBA basketball with 82 regular season games, playoffs, and NBA Finals';
      break;
    case 'NHL':
      currentSeason = getSeasonLabel('NHL');
      leagueContext = 'professional NHL hockey with 82 regular season games, playoffs, and Stanley Cup Finals';
      break;
    default:
      currentSeason = getSeasonLabel('CFB');
      leagueContext = 'college football with conference championships, bowl games, and College Football Playoff';
  }

  const prompt = `You are an expert ${teamType} travel advisor with deep knowledge of ${leagueContext}. Your task is to create a comprehensive GameDay travel plan that users will be excited to read and act on.

IMPORTANT: The team "${selectedTeam.name}" is a ${teamType} team. 

CRITICAL FIRST STEP: Search the internet for the ${currentSeason} schedule to verify the ${teamType} game "${gameInfo.game}" is actually scheduled for ${gameInfo.date}. ${getScheduleVerificationInstructions(teamType)}

Game: ${gameInfo.game}
Date: ${gameInfo.date}
Venue: ${gameInfo.venue || 'TBD'}
Kickoff: ${gameInfo.kickoff || 'TBD'}
Traveling from: ${travelFrom}
Fan team: ${selectedTeam.name} (${teamType})
${getSeasonContext(teamType, gameInfo)}
${gameInfo.isRivalry ? 'RIVALRY GAME - Extra atmosphere and traditions expected' : ''}

${getLeagueContext(teamType)}

Create an engaging, enthusiastic response that makes users excited about their trip. Use specific venue names, insider tips, and local knowledge that only someone who's been to many ${teamType} games would know.

Please provide a detailed JSON response with this exact structure:

{
  "scheduleVerification": {
    "gameExists": true/false,
    "actualDate": "correct date if different",
    "venue": "exact stadium name and city",
    "notes": "any important schedule information, ${getScheduleNotes(teamType)}"
  },
  "gameInfo": {
    "matchup": "${gameInfo.game}",
    "date": "${gameInfo.date}",
    "venue": "Specific stadium name, address, and key details about the venue",
    "kickoff": "Actual kickoff time with TV network if available",
    "atmosphere": "What makes this ${teamType} game/venue special - ${getAtmosphereDescription(teamType)}"
  },
  "tickets": [
          {
        "suggestion": "Exclusive FanProtect Guarantee - Compelling reason to book tickets with specific seating advice for ${getTicketAdvice(teamType)}",
        "link": "https://www.stubhub.com/?gcid=chAFF-_-geoUS-_-genTICKETS-_-dt170201-_-40bags",
        "partner": "StubHub",
        "details": "Specific sections to target for ${teamType} games, price ranges, and insider tips for this venue"
      },
          {
        "suggestion": "Hottest Tickets in Town - Official ${teamType === 'NFL' ? 'NFL' : 'college'} ticket marketplace benefits",
        "link": "https://www.ticketmaster.com/affiliates/?tm_link=40bags",
        "partner": "Ticketmaster", 
        "details": "${teamType === 'NFL' ? 'Season ticket holder resale options, official NFL pricing' : 'Student ticket policies, alumni allotments, official university pricing'}"
      },
      {
        "suggestion": "Life Happens Live - Premium ticket experience with verified authentic seats",
        "link": "https://vivid-seats.pxf.io/Vm3V23",
        "partner": "Vivid Seats",
        "details": "Premium seating options, last-minute availability, and mobile ticket delivery"
      }
  ],
  "travel": {
    "flights": [
      {
        "suggestion": "Explore a World of Travel - Specific flight strategy for this route with timing recommendations for ${teamType} game weekend",
        "link": "https://www.expedia.com/?affcid=EXPEDIA.AFFILIATE.40BAGS",
        "partner": "Expedia",
        "details": "Best airlines for this route, optimal departure times for ${teamType === 'NFL' ? 'professional game logistics' : 'campus weekend activities'}, and cost-saving tips"
      },
      {
        "suggestion": "Wander Wisely - Alternative airports and routing options for ${teamType} game travel",
        "link": "https://prf.hn/click/camref:1011lTcBA/creativeref:1100l110704",
        "partner": "Travelocity",
        "details": "Regional airport options, connecting flight benefits, ${teamType === 'NFL' ? 'business traveler schedules' : 'college town accessibility'}"
      }
    ],
    "hotels": [
      {
        "name": "${teamType} Game Day Experience Hotels",
        "suggestion": "Find & Book Your Perfect Stay - THE hotels where ${teamType === 'NFL' ? 'NFL fans and corporate groups' : 'visiting fans and alumni'} stay - these places will be buzzing with ${teamType === 'NFL' ? 'professional game day energy' : 'college game spirit'}! Picture ${teamType === 'NFL' ? 'business networking mixed with fan passion' : 'waking up to fight songs in the lobby and sharing elevators with alumni in vintage jerseys'}.",
        "link": "https://www.hotels.com/affiliates/hotelscom-home.VsBKzRM",
        "partner": "Hotels.com",
        "details": "Specific hotel names: [List 3-4 actual hotels with addresses]. ${teamType === 'NFL' ? 'Corporate packages, game-day shuttles, and business amenities' : 'Alumni discounts, campus shuttles, and tailgate packages'}.",
        "atmosphere": "${teamType === 'NFL' ? 'Professional energy, business networking, sophisticated fan culture' : 'Electric college energy, alumni reunions, student spirit everywhere'}"
      },
      {
        "name": "Luxury ${teamType} Weekend Escapes",
        "suggestion": "Treat this ${teamType === 'NFL' ? 'NFL experience' : 'college trip'} like the special occasion it is! These upscale properties offer the perfect balance of comfort and proximity to ${teamType === 'NFL' ? 'the professional stadium district' : 'campus activities'}.",
        "link": "https://www.booking.com/?aid=40bags",
        "partner": "Booking.com",
        "details": "Premium hotels: [List 3-4 actual upscale hotels]. ${teamType === 'NFL' ? 'Downtown locations, stadium views, executive lounges' : 'Historic properties, campus views, alumni-preferred locations'}.",
        "atmosphere": "${teamType === 'NFL' ? 'Sophisticated comfort, city skylines, perfect for client entertainment' : 'Historic charm, campus nostalgia, perfect for milestone celebrations'}"
      },
      {
        "name": "${teamType} Fan Gathering Houses",
        "suggestion": "Relax, You're Booking a Vrbo - Turn your ${teamType} trip into an epic ${teamType === 'NFL' ? 'fan experience with fellow professionals' : 'alumni reunion'}! These properties are perfect for groups who want to recreate the ultimate ${teamType === 'NFL' ? 'tailgate experience' : 'college fan cave'} away from home.",
        "link": "https://vrbo.com/affiliate/oWiAOm8",
        "partner": "VRBO",
        "details": "Group rentals: [Describe specific property types]. ${teamType === 'NFL' ? 'Houses with premium grilling setups, multiple TVs for RedZone, and spaces for corporate group bonding' : 'Houses near campus with big kitchens for pregame cooking, multiple TVs for watching other college games, and spaces where everyone can gather in their alma mater gear'}.",
        "atmosphere": "${teamType === 'NFL' ? 'Professional tailgate atmosphere, networking central, premium game day' : 'College nostalgia central, alumni bonding, memories in the making'}"
      }
    ]
  },
  "dining": [
    {
      "category": "Legendary Pre-Game Spots",
      "description": "These aren't just restaurants - they're ${teamType === 'NFL' ? 'institutions where NFL fans and business groups gather before games' : 'pilgrimage sites for college football fans and alumni'}. Every true ${teamType} fan needs to experience these places!",
      "venues": [
        "Restaurant Name (exact address) - The story: ${teamType === 'NFL' ? 'Why NFL players, executives, and longtime fans' : 'Why students, alumni, and legendary coaches'} have made this place special, signature dishes that are Instagram-worthy, and decades of ${teamType === 'NFL' ? 'professional football' : 'college football'} history",
        "Iconic Spot Name (address) - The experience: What you'll see/hear/feel here, famous ${teamType === 'NFL' ? 'NFL' : 'college'} moments that happened here, must-try items that locals swear by",
        "Local Institution (address) - The tradition: How this place connects to ${teamType === 'NFL' ? 'professional game day culture and business networking' : 'campus culture and alumni traditions'}, optimal arrival times, insider menu items"
      ]
    },
    {
      "category": "Victory (or Recovery) Dining",
      "description": "Win or lose, you'll need the perfect spot to either celebrate in style or comfort your soul. These places understand the emotional rollercoaster of ${teamType} game day.",
      "venues": [
        "Celebration Central (address) - Where champions go: ${teamType === 'NFL' ? 'The place that attracts NFL personalities, stays open late, and serves food worthy of a professional victory' : 'Where championship celebrations happen, alumni toast victories, and highlight reels play on repeat'}",
        "Comfort Food Haven (address) - Healing cuisine: The spot that understands a tough ${teamType} loss, serves portions that satisfy emotional eating, ${teamType === 'NFL' ? 'and has staff who\'ve seen corporate groups through playoff heartbreak' : 'and has comforted generations of heartbroken alumni'}",
        "Late Night Legend (address) - Extended game day: ${teamType === 'NFL' ? 'Perfect for business groups rehashing professional strategy, open until the networking winds down' : 'Open until the alma mater runs out, perfect for rehashing every play with fellow alumni'}"
      ]
    }
  ]
}

CRITICAL REQUIREMENTS:
1. Use the provided game information (team, opponent, date, venue) as accurate and confirmed
2. Use actual, specific venue names and addresses - no generic descriptions  
3. Tailor ALL content to ${teamType} culture and fan experience
4. ${teamType === 'NFL' ? 'Include professional football context: business networking, corporate packages, premium experiences' : 'Include college context: alumni connections, campus traditions, student sections, fight songs'}
5. Make hotel and dining descriptions compelling and emotional - users should feel excited reading them
6. Include insider knowledge that shows you understand ${teamType} culture
7. Write descriptions that paint a picture and create anticipation for ${teamType} game day
8. Focus on the experiential aspects specific to ${teamType} fan culture
9. Respond ONLY with valid JSON`;

  try {
    const controller = new AbortController();
    const timeoutMs = 45000;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const model = 'gpt-5-mini';
    // Route via serverless proxy to avoid client 400s and hide key
    const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
    const response = await fetch(`${apiBase}/api/openai-proxy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model,
        messages: [
          {
            role: 'system',
            content: `You are an expert ${teamType} travel advisor with deep knowledge of ${leagueContext}. You will be provided with confirmed game information from ESPN.

CRITICAL: You are working with ${teamType} teams ONLY. Focus on ${teamType === 'NFL' ? 'NFL professional football culture, stadiums, and fan experiences' : 'college football culture, campus traditions, and alumni experiences'}.

Always use the provided game information as accurate and confirmed, then provide engaging, specific recommendations tailored to ${teamType} fan culture that make users excited about their trip. Understand the differences between NFL professional atmosphere and college campus traditions. Respond with valid JSON only.`
          },
          { role: 'user', content: prompt }
        ],
        max_tokens: 3000,
        temperature: 0.4,
        top_p: 0.9,
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.text();
      try { console.error('OpenAI proxy error:', errorData); } catch {}
      throw new Error(`OpenAI proxy request failed: ${response.status} ${response.statusText} - ${errorData}`);
    }

    const data = await response.json();
    const aiResponse = data?.choices?.[0]?.message?.content || '';
    
    console.log('Raw AI Response:', aiResponse); // Debug log
    
    return aiResponse;
  } catch (error) {
    console.error('Error calling OpenAI API:', error);
    throw error;
  }
};

// Parse the AI response and structure it for display
export const parseAIResponse = (aiResponse) => {
  try {
    const original = typeof aiResponse === 'string' ? aiResponse : String(aiResponse || '');

    // 1) Strip common code fences and leading label
    let text = original.trim();
    const fenceStart = /^\s*```(?:json)?\s*/i;
    const fenceEnd = /\s*```\s*$/;
    if (fenceStart.test(text)) text = text.replace(fenceStart, '');
    if (fenceEnd.test(text)) text = text.replace(fenceEnd, '');
    if (/^json\s*[{\[]/i.test(text)) text = text.replace(/^json\s*/i, '');

    // 2) If it still fails, extract the largest { ... } block
    const tryParse = (s) => {
      const normalized = s
        .replace(/[\u201C\u201D]/g, '"') // smart quotes to straight
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/,(\s*[}\]])/g, '$1'); // remove trailing commas
      return JSON.parse(normalized);
    };

    let parsed = null;
    try {
      parsed = tryParse(text);
    } catch {
      const first = text.indexOf('{');
      const last = text.lastIndexOf('}');
      if (first !== -1 && last !== -1 && last > first) {
        const sub = text.slice(first, last + 1);
        try { parsed = tryParse(sub); } catch {}
      }
    }

    if (!parsed) {
      // 3) Section-wise regex extraction to salvage partial content
      const safeParse = (s) => {
        try { return JSON.parse(s); } catch { return null; }
      };
      const extractObject = (key) => {
        const m = text.match(new RegExp(`"${key}"\s*:\s*\{[\s\S]*?\}`, 'i'));
        if (!m) return null;
        const objText = m[0].replace(new RegExp(`^\s*"${key}"\s*:\s*`,'i'), '');
        return safeParse(objText);
      };
      const extractArray = (key) => {
        const m = text.match(new RegExp(`"${key}"\s*:\s*\[[\s\S]*?\]`, 'i'));
        if (!m) return null;
        const arrText = m[0].replace(new RegExp(`^\s*"${key}"\s*:\s*`,'i'), '');
        return safeParse(arrText);
      };

      const salvage = {
        scheduleVerification: extractObject('scheduleVerification') || {},
        gameInfo: extractObject('gameInfo') || {},
        tickets: extractArray('tickets') || [],
        travel: extractObject('travel') || { flights: [], hotels: [] },
        dining: extractArray('dining') || [],
      };
      parsed = salvage;
    }

    if (parsed && typeof parsed === 'object') {
      // Normalize/soft-require sections; fill sane defaults so UI renders even if partial
      const normalized = {
        scheduleVerification: parsed.scheduleVerification || {},
        gameInfo: parsed.gameInfo || {},
        tickets: Array.isArray(parsed.tickets) ? parsed.tickets : [],
        travel: parsed.travel || { flights: [], hotels: [] },
        dining: Array.isArray(parsed.dining) ? parsed.dining : [],
      };
      return { ...normalized, rawResponse: original };
    }

    // Fallback: extract best-effort sections from text
    const fallback = extractFromTextResponse(original);
    return { ...fallback, parsingError: true, rawResponse: original };

  } catch (error) {
    console.error('Error parsing AI response:', error);
    const fallback = extractFromTextResponse(String(aiResponse || ''));
    return { ...fallback, parsingError: true, rawResponse: aiResponse };
  }
};

// Fallback text extraction function
const extractFromTextResponse = (textResponse) => {
  console.log('Attempting text extraction from:', textResponse.substring(0, 200) + '...');
  
  // Try to extract basic information from text
  const lines = textResponse.split('\n');
  
  return {
    gameInfo: {
      matchup: "Information available in AI response",
      date: "Check AI response for details",
      venue: "Venue information provided by AI",
      kickoff: "Game time in AI response"
    },
    tickets: [
      {
        suggestion: "AI provided ticket recommendations (see full response below)",
        link: "https://www.stubhub.com/?gcid=chAFF-_-geoUS-_-genTICKETS-_-dt170201-_-40bags",
        partner: "StubHub",
        details: "Check raw AI response for specific details"
      }
    ],
    travel: {
      flights: [
        {
          suggestion: "AI provided flight recommendations (see full response below)",
          link: "https://www.expedia.com/?affcid=EXPEDIA.AFFILIATE.40BAGS",
          partner: "Expedia",
          details: "Check raw AI response for specific flight advice"
        }
      ],
      hotels: [
        {
          name: "AI recommended hotels",
          suggestion: "See full AI response for hotel details",
          link: "https://www.hotels.com/?affid=40bags",
          partner: "Hotels.com",
          details: "Specific recommendations in AI response"
        }
      ]
    },
    dining: [
      {
        category: "AI Dining Recommendations",
        venues: [
          "AI provided restaurant recommendations",
          "See raw response below for specific venues",
          "Check full AI output for dining details"
        ]
      }
    ],
    textFallback: true,
    rawResponse: textResponse
  };
};

// Create environment variable instructions
export const getAPIKeyInstructions = () => {
  return `To enable AI-powered travel recommendations (server-side):

1. Get an OpenAI API key from https://platform.openai.com/api-keys
2. In your deployment environment (e.g., Vercel), set an environment variable:
   OPENAI_API_KEY=your_api_key_here
3. Redeploy or restart your dev server

All client requests will go through /api/openai-proxy to keep the key server-side.`;
};

// Generate an AI Tailgate Guide with structured JSON output
export const generateTailgateGuide = async (teamName, game) => {
  const prompt = `You are a seasoned tailgate strategist. Create a city-and-venue specific tailgate guide strictly for this game.

TEAM: ${teamName}
OPPONENT: ${game?.opponent || ''}
DATE: ${game?.date || ''}
VENUE: ${game?.venue || ''}
KICKOFF: ${game?.kickoff || ''}

REQUIREMENTS:
- Provide actionable, current best-practice guidance tailored to the venue/city.
- Use specific venue names and known policies where possible; avoid generic platitudes.
- If unsure of a detail, omit it or mark it as “verify on official site”.
- Be concise but practical.
- IMPORTANT: Cover tailgating by stadium direction (North, East, South, West). Many venues have lots/zones that differ in crowd vibe, setup rules, and timing. Include directional guidance and any notable lots.

Respond ONLY with a valid JSON object using this exact schema:
{
  "stadium": { "bagPolicy": "string", "gates": "string", "concessions": "string" },
  "parking": { "officialLots": "string", "walkTimes": "string", "rideshare": "string", "directionalLots": { "north": "string", "east": "string", "south": "string", "west": "string" } },
  "tailgateZones": ["string", "string"],
  "barsAndFood": [ { "name": "string", "address": "string", "hours": "string", "notes": "string" } ],
  "timeline": [ { "time": "HH:MM", "activity": "string" } ],
  "checklist": ["string"],
  "tips": ["string"],
  "citations": ["https://..."],
  "lastUpdated": "YYYY-MM-DD"
}`;

  try {
    const controller = new AbortController();
    const timeoutMs = 30000;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:3001' : '';
    const response = await fetch(`${apiBase}/api/openai-proxy`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'gpt-5-mini',
        messages: [
          { role: 'system', content: 'You are a precise assistant that outputs only valid JSON matching the requested schema.' },
          { role: 'user', content: prompt }
        ],
        max_tokens: 1400,
        temperature: 0.3
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    if (!response.ok) throw new Error(`Tailgate guide request failed: ${response.status}`);
    const data = await response.json();
    return data?.choices?.[0]?.message?.content || '';
  } catch (e) {
    console.error('generateTailgateGuide error', e);
    throw e;
  }
};

export const parseTailgateGuide = (aiText) => {
  try {
    const original = String(aiText || '').trim();
    const fenceStart = /^```(?:json)?/i;
    const fenceEnd = /```\s*$/;
    let text = original.replace(fenceStart, '').replace(fenceEnd, '').trim();
    const normalized = text
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/,(\s*[}\]])/g, '$1');
    const parsed = JSON.parse(normalized);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return {
      stadium: { bagPolicy: 'Verify on official site', gates: '', concessions: '' },
      parking: { officialLots: '', walkTimes: '', rideshare: '' },
      tailgateZones: [],
      barsAndFood: [],
      timeline: [],
      checklist: [],
      tips: [],
      citations: [],
      lastUpdated: new Date().toISOString().slice(0, 10)
    };
  }
};